/**
 * Side panel, for the source viewer and record detail.
 *
 * Not a modal: the whole point is reading the panel *against* what is behind it — an answer beside
 * the passage it cites. A modal would hide the thing being compared.
 *
 * It docks to the inline end, so it appears on the right in English and the left in Arabic without
 * a second layout.
 */

import type { ReactNode } from "react";

import { cx } from "./cx";
import { CloseIcon } from "./icons";

export interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  closeLabel: string;
  className?: string;
}

export function SidePanel({
  open,
  onClose,
  title,
  children,
  closeLabel,
  className,
}: SidePanelProps): JSX.Element | null {
  if (!open) return null;

  return (
    <aside
      // `complementary`, not `dialog`: it does not trap focus, because the reader is meant to move
      // between the answer and the source freely.
      aria-labelledby="side-panel-title"
      className={cx(
        "flex w-full shrink-0 flex-col border-s border-borderSubtle bg-surface lg:w-panel",
        className,
      )}
    >
      {/* Same height as the shell's header and the rail's brand block, so the three rules across
          the top of the window line up instead of nearly lining up. */}
      <header className="flex h-header shrink-0 items-center justify-between gap-3 border-b border-borderSubtle px-4">
        <h2 id="side-panel-title" className="text-heading-sm font-bold text-primary">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-borderSubtle bg-surfaceRaised text-textMuted hover:bg-primarySubtle hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
        >
          <CloseIcon />
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
    </aside>
  );
}
